import { useState, useEffect, useCallback } from 'react';
import { storage } from '../utils/storage';
import { FEATURE_FLAGS, type FeatureName } from '../utils/features';
import { useAuthContext } from '../contexts/AuthContext';
import { logger } from '../utils/logger';
import { STORAGE_KEYS, createFeatureKey } from '../constants/storageKeys';
import { MESSAGE_TYPES } from '../constants/background';
import { getPlatform } from '../infrastructure/platform';

export function useFeatures() {
  const { currentUser } = useAuthContext();
  const [enabledFeatures, setEnabledFeatures] = useState<Record<FeatureName, boolean>>(
    {} as Record<FeatureName, boolean>
  );
  const [isLoading, setIsLoading] = useState(true);

  const loadFeatureStates = useCallback(async () => {
    try {
      // Check if storage is initialized
      if (!storage._isInitialized()) {
        logger.warning('Storage not initialized, using default feature flags', {
          module: 'features',
          function: 'loadFeatureStates',
        });
        const defaultStates = Object.keys(FEATURE_FLAGS).reduce(
          (acc, feature) => ({
            ...acc,
            [feature]: FEATURE_FLAGS[feature as FeatureName],
          }),
          {} as Record<FeatureName, boolean>
        );
        setEnabledFeatures(defaultStates);
        setIsLoading(false);
        return;
      }

      // Get settings from storage
      const settings = await storage.get(STORAGE_KEYS.SETTINGS);
      const isPremium = settings?.settings?.premium_status === 'paid';

      logger.debug('Loading feature states', {
        module: 'features',
        function: 'loadFeatureStates',
        isPremium,
        premiumStatus: settings?.settings?.premium_status,
      });

      // Load user preferences from storage
      const userPreferences = await Promise.all(
        Object.keys(FEATURE_FLAGS).map(async feature => {
          const storedPref = await storage.get(createFeatureKey(feature));
          return {
            feature,
            enabled:
              storedPref !== null ? storedPref : isPremium || FEATURE_FLAGS[feature as FeatureName],
          };
        })
      );

      // Build feature states object respecting user preferences
      const states = userPreferences.reduce(
        (acc, { feature, enabled }) => ({
          ...acc,
          [feature]: enabled,
        }),
        {} as Record<FeatureName, boolean>
      );

      setEnabledFeatures(states);
    } catch (error) {
      logger.error(
        'Error loading feature states',
        error instanceof Error ? error : new Error('Unknown error'),
        {
          module: 'features',
          function: 'loadFeatureStates',
        }
      );
      // Fall back to default feature flags
      const defaultStates = Object.keys(FEATURE_FLAGS).reduce(
        (acc, feature) => ({
          ...acc,
          [feature]: FEATURE_FLAGS[feature as FeatureName],
        }),
        {} as Record<FeatureName, boolean>
      );
      setEnabledFeatures(defaultStates);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const toggleFeature = useCallback(
    async (feature: FeatureName) => {
      try {
        const newState = !enabledFeatures[feature];
        setEnabledFeatures(prev => ({ ...prev, [feature]: newState }));
        await storage.set(createFeatureKey(feature), newState);
      } catch (error) {
        logger.error(
          'Error toggling feature',
          error instanceof Error ? error : new Error('Unknown error'),
          {
            module: 'features',
            function: 'toggleFeature',
            feature,
          }
        );
        // Revert state on error
        setEnabledFeatures(prev => ({ ...prev, [feature]: !prev[feature] }));
      }
    },
    [enabledFeatures]
  );

  useEffect(() => {
    // Load initial feature states
    loadFeatureStates();

    // Listen for premium status updates
    const handlePremiumUpdate = async (message: any) => {
      if (
        message.type === MESSAGE_TYPES.SETTINGS_UPDATE ||
        message.type === MESSAGE_TYPES.PREMIUM_STATUS_UPDATE
      ) {
        logger.debug('Premium status updated, reloading features', {
          module: 'features',
          function: 'handlePremiumUpdate',
          messageType: message.type,
          isPremium: message.isPremium,
        });
        await loadFeatureStates();
      }
    };

    // Add listener for premium status updates; unsubscribe on unmount
    return getPlatform().bus.subscribe(handlePremiumUpdate);
  }, [currentUser, loadFeatureStates]); // Re-run when user changes

  return {
    enabledFeatures,
    toggleFeature,
    isLoading,
  };
}
